import * as THREE from 'three';
import {
  GLOW_LIGHT_CULL_DISTANCE,
  GLOW_LIGHT_INTENSITY_SCALE,
  GLOW_LIGHT_RANGE,
  MAX_ACTIVE_GLOW_LIGHTS,
} from '@shared/protocol';

interface GlowSource {
  x: number;
  y: number;
  z: number;
  color: string;
  intensity: number;
}

/**
 * Pooled, unshadowed point lights for glowing solids / custom glow voxels.
 * Performance: hard cap, distance cull, no shadow maps, finite range.
 */
export class GlowLightManager {
  readonly group = new THREE.Group();

  private readonly sources = new Map<string, GlowSource>();
  private readonly lights: THREE.PointLight[] = [];
  private readonly scratch = new THREE.Vector3();
  private enabled = true;

  constructor() {
    for (let i = 0; i < MAX_ACTIVE_GLOW_LIGHTS; i++) {
      const light = new THREE.PointLight(0xffffff, 0, GLOW_LIGHT_RANGE, 2);
      light.castShadow = false;
      light.visible = false;
      this.lights.push(light);
      this.group.add(light);
    }
  }

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (!enabled) {
      for (const light of this.lights) {
        light.visible = false;
        light.intensity = 0;
      }
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setSource(
    key: string,
    x: number,
    y: number,
    z: number,
    color: string,
    intensity: number,
  ): void {
    if (intensity <= 0) {
      this.sources.delete(key);
      return;
    }
    this.sources.set(key, {
      x: x + 0.5,
      y: y + 0.5,
      z: z + 0.5,
      color,
      intensity: Math.min(1, intensity),
    });
  }

  removeSource(key: string): void {
    this.sources.delete(key);
  }

  removeSourcesWithPrefix(prefix: string): void {
    for (const key of this.sources.keys()) {
      if (key.startsWith(prefix)) this.sources.delete(key);
    }
  }

  clear(): void {
    this.sources.clear();
    for (const light of this.lights) {
      light.visible = false;
      light.intensity = 0;
    }
  }

  /** Pick the nearest sources and bind them to the pooled lights. */
  update(cameraX: number, cameraY: number, cameraZ: number): void {
    if (!this.enabled) return;
    const cullSq = GLOW_LIGHT_CULL_DISTANCE * GLOW_LIGHT_CULL_DISTANCE;
    const ranked: Array<{ source: GlowSource; distSq: number }> = [];
    for (const source of this.sources.values()) {
      const dx = source.x - cameraX;
      const dy = source.y - cameraY;
      const dz = source.z - cameraZ;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (distSq > cullSq) continue;
      ranked.push({ source, distSq });
    }
    ranked.sort((a, b) => a.distSq - b.distSq);

    for (let i = 0; i < this.lights.length; i++) {
      const light = this.lights[i]!;
      const entry = ranked[i];
      if (!entry) {
        light.visible = false;
        light.intensity = 0;
        continue;
      }
      const { source } = entry;
      light.visible = true;
      light.color.set(source.color);
      light.intensity = source.intensity * GLOW_LIGHT_INTENSITY_SCALE;
      light.distance = Math.max(2, GLOW_LIGHT_RANGE * (0.35 + 0.65 * source.intensity));
      light.position.set(source.x, source.y, source.z);
      this.scratch.copy(light.position);
    }
  }
}
